import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Upload, 
  FileText, 
  RefreshCw, 
  Archive, 
  Trash2, 
  Copy,
  Loader2,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import confetti from "canvas-confetti";

interface ReplaceDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  originalDocument: {
    id: string;
    title: string;
    doc_type: string | null;
    file_path?: string | null;
  };
  onSuccess: () => void;
}

type Step = 'upload' | 'confirm';
type OldDocAction = 'archive' | 'delete' | 'keep';

export function ReplaceDocumentDialog({
  open,
  onOpenChange,
  originalDocument,
  onSuccess,
}: ReplaceDocumentDialogProps) {
  const [step, setStep] = useState<Step>('upload');
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [newDocId, setNewDocId] = useState<string | null>(null);
  const [newDocTitle, setNewDocTitle] = useState<string>('');
  const [oldDocAction, setOldDocAction] = useState<OldDocAction>('archive');

  const resetState = () => {
    setStep('upload');
    setUploading(false);
    setProcessing(false);
    setUploadedFile(null);
    setNewDocId(null);
    setNewDocTitle('');
    setOldDocAction('archive');
  };

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    setUploadedFile(file);
    setUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}_${file.name}`;
      
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Create document record - doc_type can be null, so we handle that
      const insertData: Record<string, unknown> = {
        user_id: user.id,
        title: file.name.replace(/\.[^/.]+$/, ''),
        file_path: fileName,
        mime_type: file.type,
        file_size: file.size,
        source: 'upload',
      };
      
      if (originalDocument.doc_type) {
        insertData.doc_type = originalDocument.doc_type;
      }

      const { data: newDoc, error: insertError } = await supabase
        .from('documents')
        .insert([insertData as any])
        .select()
        .single();

      if (insertError) throw insertError;

      setNewDocId(newDoc.id);
      setNewDocTitle(newDoc.title);
      setStep('confirm');

    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Erreur lors de l'upload");
      setUploadedFile(null);
    } finally {
      setUploading(false);
    }
  }, [originalDocument.doc_type]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
    },
    maxFiles: 1,
    disabled: uploading,
  });

  const handleConfirm = async () => {
    if (!newDocId) return;
    
    setProcessing(true);

    try {
      // Handle old document based on user choice
      if (oldDocAction === 'delete') {
        // Soft delete - move to trash
        await supabase
          .from('documents')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', originalDocument.id);
      } else if (oldDocAction === 'archive') {
        // Dismiss expiry alert (no 'archived' column exists in schema)
        await supabase
          .from('documents')
          .update({ 
            expiry_dismissed_at: new Date().toISOString(),
          })
          .eq('id', originalDocument.id);
      }
      // 'keep' = do nothing to old document

      // Celebration!
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      toast.success("Document remplacé avec succès ! 🎉");
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error processing replacement:', error);
      toast.error("Erreur lors du remplacement");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-primary" />
            Remplacer le document
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' 
              ? `Uploadez la nouvelle version de "${originalDocument.title}"`
              : "Nouveau document détecté. Que faire de l'ancien ?"
            }
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="py-4">
            <div
              {...getRootProps()}
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
                isDragActive && "border-primary bg-primary/5",
                uploading && "opacity-50 cursor-not-allowed",
                !isDragActive && !uploading && "border-muted-foreground/25 hover:border-primary/50"
              )}
            >
              <input {...getInputProps()} />
              
              {uploading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 text-primary animate-spin" />
                  <p className="text-sm text-muted-foreground">Upload en cours...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <Upload className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {isDragActive ? "Déposez le fichier ici" : "Glissez-déposez ou cliquez"}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      PDF, images (max 10MB)
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Current document info */}
            <div className="mt-4 p-3 bg-muted/50 rounded-lg flex items-center gap-3">
              <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{originalDocument.title}</p>
                <p className="text-xs text-muted-foreground">Document actuel</p>
              </div>
              <Badge variant="outline" className="shrink-0 text-warning border-warning">
                À remplacer
              </Badge>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div className="py-4 space-y-4">
            {/* Visual comparison */}
            <div className="flex items-center gap-3">
              {/* Old document */}
              <div className="flex-1 p-3 bg-muted/50 rounded-lg border border-muted">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Ancien</span>
                </div>
                <p className="text-sm font-medium truncate">{originalDocument.title}</p>
              </div>
              
              <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0" />
              
              {/* New document */}
              <div className="flex-1 p-3 bg-success/10 rounded-lg border border-success/30">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-success" />
                  <span className="text-xs text-success">Nouveau</span>
                </div>
                <p className="text-sm font-medium truncate">{newDocTitle}</p>
              </div>
            </div>

            {/* Action choice for old document */}
            <div className="space-y-3">
              <p className="text-sm font-medium">Que faire de l'ancien document ?</p>
              
              <RadioGroup 
                value={oldDocAction} 
                onValueChange={(v) => setOldDocAction(v as OldDocAction)}
                className="space-y-2"
              >
                <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="archive" id="archive" />
                  <Label htmlFor="archive" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Archive className="w-4 h-4 text-muted-foreground" />
                      <span>Archiver</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Conserve l'ancien document dans les archives
                    </p>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="delete" id="delete" />
                  <Label htmlFor="delete" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4 text-destructive" />
                      <span>Supprimer</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Déplace l'ancien document vers la corbeille
                    </p>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="keep" id="keep" />
                  <Label htmlFor="keep" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Copy className="w-4 h-4 text-muted-foreground" />
                      <span>Garder les deux</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Conserve les deux versions du document
                    </p>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={processing}>
            Annuler
          </Button>
          {step === 'confirm' && (
            <Button onClick={handleConfirm} disabled={processing}>
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Traitement...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Confirmer
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
